import {
  Component, OnInit, Inject, inject,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';

import { UsersService } from '../../../service/entity-services/users.service';
import { EmailService, SendEmailRequest, EmailAttachment } from '../../../service/communications/email.service';
import { environment } from '../../../../environments/environment';
import { formatDateCell } from '../../utils/format-date-cell';

export interface SendReportDialogData {
  reportName: string;
  gridApi: any;
}

@Component({
  selector: 'app-send-report-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDividerModule
  ],
  template: `
    <div class="send-dialog">
      <h2 class="dialog-title">
        <mat-icon>send</mat-icon>
        Send Report
      </h2>

      <mat-divider></mat-divider>

      <div class="dialog-body">
        <!-- Subject -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Subject</mat-label>
          <input matInput [(ngModel)]="subject" />
        </mat-form-field>

        <!-- Internal Users -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Send to users</mat-label>
          <mat-select [(ngModel)]="selectedUsers" multiple>
            @for (user of allUsers; track user.email) {
              <mat-option [value]="user.email">{{ user.friendlyName || user.email }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select users from your organization</mat-hint>
        </mat-form-field>

        <!-- External Emails -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>External email addresses</mat-label>
          <input matInput
                 [(ngModel)]="externalEmails"
                 placeholder="comma-separated emails" />
          <mat-hint>For recipients outside your organization</mat-hint>
        </mat-form-field>

        <!-- Optional Message -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Message (optional)</mat-label>
          <textarea matInput [(ngModel)]="message" rows="3" placeholder="Add a personal note..."></textarea>
        </mat-form-field>

        <!-- Row count info -->
        <p class="row-info">
          <mat-icon>table_rows</mat-icon>
          {{ totalRows }} rows will be included in the email
          @if (totalRows > maxRows) {
            <span class="truncate-note">(email previews the first {{ maxRows }}; all {{ totalRows }} rows are in the attached spreadsheet)</span>
          }
        </p>
      </div>

      <mat-divider></mat-divider>

      <div class="dialog-actions">
        @if (sending) {
          <mat-spinner diameter="24"></mat-spinner>
          <span class="sending-text">Sending...</span>
        } @else {
          <button mat-button (click)="close()">Cancel</button>
          <button mat-raised-button color="primary"
                  (click)="send()"
                  [disabled]="!hasRecipients()">
            <mat-icon>send</mat-icon> Send Email
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    .send-dialog {
      width: 500px;
      max-width: 90vw;
    }

    .dialog-title {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0;
      padding: 20px 24px;
      font-size: 20px;
      font-weight: 500;
    }

    .dialog-title mat-icon {
      color: #1976d2;
    }

    .dialog-body {
      padding: 20px 24px;
    }

    .full-width {
      width: 100%;
      margin-bottom: 8px;
    }

    .row-info {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #666;
      margin: 8px 0 0;
    }

    .row-info mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #999;
    }

    .truncate-note {
      color: #e65100;
      font-size: 12px;
    }

    .dialog-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding: 12px 24px;
    }

    .sending-text {
      font-size: 14px;
      color: #666;
    }
  `]
})
export class SendReportDialogComponent implements OnInit {

  private usersService = inject(UsersService);
  private emailService = inject(EmailService);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);

  allUsers: any[] = [];
  selectedUsers: string[] = [];
  externalEmails = '';
  subject = '';
  message = '';
  sending = false;
  totalRows = 0;
  readonly maxRows = 100;

  constructor(
    public dialogRef: MatDialogRef<SendReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendReportDialogData
  ) {}

  ngOnInit(): void {
    this.subject = `Report: ${this.data.reportName}`;
    this.totalRows = this.data.gridApi?.getDisplayedRowCount() || 0;

    this.usersService.allUsers$.subscribe(users => {
      this.allUsers = users || [];
      this.cdr.markForCheck();
    });
  }

  hasRecipients(): boolean {
    return this.selectedUsers.length > 0 || this.externalEmails.trim().length > 0;
  }

  async send(): Promise<void> {
    const recipients = this.getAllRecipients();
    if (recipients.length === 0) return;

    this.sending = true;
    this.cdr.markForCheck();

    const htmlBody = this.buildEmailHtml();

    // Build the workbook ONCE — the same bytes go to every recipient, and it can
    // be several MB. A failure here must not block the email: recipients still
    // get the inline preview.
    let attachments: EmailAttachment[] | undefined;
    try {
      attachments = await this.buildExcelAttachment();
    } catch (err) {
      console.error('[SendReport] Excel generation failed, sending without attachment:', err);
      this.snackBar.open('Could not attach the spreadsheet — sending the summary only.', 'Dismiss', { duration: 5000 });
    }

    const requests = recipients.map(email => {
      const payload: SendEmailRequest = {
        to: email,
        subject: this.subject,
        htmlBody,
        ...(attachments && { attachments })
      };
      return this.emailService.send(payload);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.snackBar.open(
          `Report sent to ${recipients.length} ${recipients.length === 1 ? 'recipient' : 'recipients'}`,
          'Dismiss',
          { duration: 4000 }
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('[SendReport] Failed to send:', err);
        this.snackBar.open('Failed to send report. Please try again.', 'Dismiss', { duration: 4000 });
        this.sending = false;
        this.cdr.markForCheck();
      }
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  /**
   * Export the CURRENT grid view (columns, filters, sort as the user sees them)
   * to .xlsx and hand it back as a base64 email attachment.
   *
   * Uses AG Grid Enterprise's getDataAsExcel(), so the attachment matches the
   * on-screen report exactly — unlike the inline HTML preview, it carries every
   * row, not just the first `maxRows`.
   *
   * Returns undefined (no attachment, email still sends) when the grid is
   * unavailable, empty, or the file is too large to post.
   */
  private async buildExcelAttachment(): Promise<EmailAttachment[] | undefined> {
    const gridApi: any = this.data.gridApi;
    if (!gridApi || typeof gridApi.getDataAsExcel !== 'function') return undefined;
    if (this.totalRows === 0) return undefined;

    const blob: Blob | undefined = gridApi.getDataAsExcel({
      fileName: this.excelFileName(),
      sheetName: (this.data.reportName || 'Report').replace(/[:\\/?*[\]]/g, '').slice(0, 28) || 'Report'
    });
    if (!blob) return undefined;

    // API Gateway caps a request body at 10MB and base64 inflates by ~1.37x, so
    // guard well under it. Better a summary-only email than a request that dies
    // with an opaque 413.
    const base64 = await this.blobToBase64(blob);
    const MAX_BASE64_BYTES = 7 * 1024 * 1024;
    if (base64.length > MAX_BASE64_BYTES) {
      this.snackBar.open(
        `This report is too large to attach (${(base64.length / 1048576).toFixed(1)}MB). ` +
        `Sending the summary only — use Export to download the full spreadsheet.`,
        'Dismiss',
        { duration: 7000 }
      );
      return undefined;
    }

    return [{
      filename: this.excelFileName(),
      content: base64,
      contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }];
  }

  /** "Expiring Contracts 2026-08-15.xlsx" — safe for a Content-Disposition header. */
  private excelFileName(): string {
    const safe = (this.data.reportName || 'report').replace(/[^\w\s-]/g, '').trim().slice(0, 60) || 'report';
    return `${safe} ${new Date().toISOString().slice(0, 10)}.xlsx`;
  }

  /** FileReader gives "data:<type>;base64,<payload>" — the backend wants only the payload. */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read workbook'));
      reader.readAsDataURL(blob);
    });
  }

  private getAllRecipients(): string[] {
    const recipients = [...this.selectedUsers];
    if (this.externalEmails.trim()) {
      const external = this.externalEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e.includes('@'));
      recipients.push(...external);
    }
    return [...new Set(recipients)]; // deduplicate
  }

  /**
   * The display text for one cell, as the GRID renders it.
   *
   * This must go through the grid rather than reading `rowNode.data[field]`,
   * because a column's on-screen value frequently isn't a plain property lookup:
   *   - `valueGetter` columns (Legal Entity, Party TIN, owner names, …) have no
   *     usable raw value at all — `legalEntities` is an array of objects.
   *   - dotted fields like `directVendor.vendorData.name` are PATHS; a bracket
   *     lookup on that string returns undefined, not the nested value.
   * Reading raw is what left those columns blank in the email while the attached
   * spreadsheet — which uses the grid's own export — showed them correctly.
   * `useFormatter: true` applies any valueFormatter, so the two now agree by
   * construction instead of by two parallel implementations.
   */
  private cellText(gridApi: any, rowNode: any, col: { colId: string; field: string }): string {
    let val: any;
    try {
      val = gridApi.getCellValue({ rowNode, colKey: col.colId, useFormatter: true });
    } catch {
      // Older/!enterprise API surface, or a column the grid can't resolve.
      val = undefined;
    }

    // Fall back to the raw row only when the grid gives us nothing, and only for
    // a simple (non-dotted) field, where a direct lookup is actually meaningful.
    if ((val === undefined || val === null || val === '') && col.field && !col.field.includes('.')) {
      val = rowNode?.data?.[col.field];
    }

    if (val === null || val === undefined) return '';
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    if (Array.isArray(val)) return val.map(v => (v == null ? '' : String(v))).filter(Boolean).join(', ');
    if (typeof val === 'object') return '';   // defensive: never emit "[object Object]"
    return String(val);
  }

  /** Cell values land inside HTML — escape so a stray "<" or "&" can't mangle the table. */
  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildEmailHtml(): string {
    const gridApi = this.data.gridApi;
    if (!gridApi) return '<p>No data available.</p>';

    // Get visible columns
    const columnState = gridApi.getColumnState();
    const visibleCols = columnState
      .filter((c: any) => !c.hide)
      .map((c: any) => {
        const col = gridApi.getColumn(c.colId);
        return {
          colId: c.colId,
          headerName: col?.getColDef()?.headerName || c.colId,
          field: col?.getColDef()?.field || c.colId
        };
      })
      .filter((c: any) => c.headerName); // skip columns without headers

    // Keep the ROW NODES, not `rowNode.data`. The cell values below are read back
    // through the grid, which needs the node — and reading the raw data is exactly
    // what made this table disagree with the attached spreadsheet.
    const rowNodes: any[] = [];
    const rowCount = Math.min(gridApi.getDisplayedRowCount(), this.maxRows);
    for (let i = 0; i < rowCount; i++) {
      const rowNode = gridApi.getDisplayedRowAtIndex(i);
      if (rowNode) {
        rowNodes.push(rowNode);
      }
    }

    // Build HTML table
    const headerCells = visibleCols
      .map((c: any) => `<th style="padding:8px 12px;text-align:left;border-bottom:2px solid #1976d2;font-size:13px;color:#333;white-space:nowrap;">${this.escapeHtml(c.headerName)}</th>`)
      .join('');

    const bodyRows = rowNodes.map((rowNode, idx) => {
      const bgColor = idx % 2 === 0 ? '#fff' : '#f9f9f9';
      const cells = visibleCols
        .map((c: any) => {
          let val = this.cellText(gridApi, rowNode, c);

          // Safety net for a column that has no valueFormatter of its own — date
          // columns now do, so this rarely fires. Shared with the grid formatter so
          // an emailed date can never disagree with the same date in the workbook.
          if (val && /^\d{4}-\d{2}-\d{2}/.test(val)) {
            val = formatDateCell(val);
          }
          if (val.length > 100) {
            val = val.substring(0, 100) + '...';
          }
          return `<td style="padding:6px 12px;border-bottom:1px solid #eee;font-size:12px;color:#555;">${this.escapeHtml(val)}</td>`;
        })
        .join('');
      return `<tr style="background:${bgColor};">${cells}</tr>`;
    }).join('');

    const messageHtml = this.message.trim()
      ? `<p style="color:#333;font-size:14px;margin:0 0 20px;line-height:1.6;">${this.message.replace(/\n/g, '<br>')}</p>`
      : '';

    // The attached workbook carries every row, so the preview cap is no longer
    // something the recipient has to work around — point them at the attachment.
    const truncateNote = this.totalRows > this.maxRows
      ? `<p style="color:#e65100;font-size:12px;margin:12px 0 0;text-align:center;">Preview shows ${this.maxRows} of ${this.totalRows} rows — the attached spreadsheet contains all ${this.totalRows}.</p>`
      : '';

    return `
      <div style="font-family:Arial,sans-serif;max-width:900px;margin:0 auto;">
        <div style="background:#1976d2;color:white;padding:16px 24px;border-radius:8px 8px 0 0;">
          <h2 style="margin:0;font-size:18px;">${this.subject}</h2>
        </div>
        <div style="border:1px solid #e0e0e0;border-top:none;padding:24px;border-radius:0 0 8px 8px;">
          ${messageHtml}
          <div style="overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;border:1px solid #e0e0e0;">
              <thead><tr style="background:#f5f5f5;">${headerCells}</tr></thead>
              <tbody>${bodyRows}</tbody>
            </table>
          </div>
          ${truncateNote}
          <p style="color:#999;font-size:11px;margin:20px 0 0;text-align:center;">
            Sent from Healthy Contracts
          </p>
        </div>
      </div>
    `;
  }
}
